import React, { useEffect, useState } from "react";
import { Spin, Empty, Pagination, message } from "antd";
import {
  CalendarOutlined,
  UserOutlined,
  ThunderboltOutlined,
  SendOutlined,
  SafetyCertificateOutlined,
  IdcardOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { platformApi } from "../../../../api";
import { useBranch } from "../../../../ContextAPI/branch";
import "../index.less";

const HistoryCol = ({ icon, iconClass, label, value }) => (
  <div className="register-history-card__col">
    <div className="register-history-card__col-inner">
      {icon ? (
        <span
          className={`register-history-card__icon${
            iconClass ? ` ${iconClass}` : ""
          }`}
        >
          {icon}
        </span>
      ) : null}
      <div className="register-history-card__col-text">
        <span className="register-history-card__label">{label}</span>
        <div className="register-history-card__value">{value}</div>
      </div>
    </div>
  </div>
);

const BookingHistoryTable = () => {
  const { selectBranch } = useBranch();
  const [history, setHistory] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [loading, setLoading] = useState(false);

  const fetchHistory = async (page = 1, size = 10) => {
    try {
      if (!selectBranch || selectBranch.length === 0) return;
      setLoading(true);

      const branch = selectBranch.map((b) => b.id);
      const res = await platformApi.post(`/api/booking/history`, {
        branch,
        page,
        size,
      });

      const { data, pagination: pageInfo } = res.data;
      setHistory(data || []);
      setPagination({
        current: pageInfo?.page || page,
        pageSize: pageInfo?.size || size,
        total: pageInfo?.total || 0,
      });
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch booking history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectBranch]);

  const formatStatus = (status) => {
    if (!status) return "-";
    const s = status.toUpperCase();
    if (s === "PENDING") return "Unauthorize";
    if (s === "ACCEPTED") return "Authorize";
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  };

  const handlePageChange = (page, pageSize) => {
    fetchHistory(page, pageSize || pagination.pageSize);
  };

  return (
    <div className="register-history-list register-record-list">
      <Spin spinning={loading}>
        {!history || history.length === 0 ? (
          <Empty description="No history" style={{ margin: "40px 0" }} />
        ) : (
          <div className="register-record-cards">
            {history.map((record, index) => {
              const serial =
                (pagination.current - 1) * pagination.pageSize + (index + 1);
              const bookingId = record?.booking?.bookingId || "-";
              const dateValue = record?.createdAt
                ? moment(new Date(record.createdAt)).format("DD/MM/YYYY HH:mm")
                : "-";
              const customer = record?.booking?.customer?.name || "-";
              const action = record?.module || "-";
              const fromStatus = formatStatus(record?.fromStatus);
              const toStatus = formatStatus(record?.toStatus);
              const changedBy =
                record?.changedBy?.profile?.employeeName || "-";

              return (
                <div
                  key={record.id}
                  className={`register-record-card register-history-card${
                    index % 2 === 1 ? " register-record-card--alt" : ""
                  }`}
                >
                  <div className="register-history-card__body">
                    <div className="register-history-card__sno">
                      <span className="register-history-card__sno-label">
                        S.NO
                      </span>
                      <span className="register-history-card__sno-value">
                        {serial}
                      </span>
                    </div>

                    <div className="register-history-card__cols">
                      <div className="register-history-card__col register-history-card__col--booking">
                        <div className="register-history-card__meta-row">
                          <span className="register-history-card__icon">
                            <CalendarOutlined />
                          </span>
                          <div className="register-history-card__col-text">
                            <span className="register-history-card__label">
                              Booking ID
                            </span>
                            <div className="register-history-card__value">
                              {bookingId}
                            </div>
                          </div>
                        </div>
                        <div className="register-history-card__meta-row">
                          <span className="register-history-card__icon">
                            <CalendarOutlined />
                          </span>
                          <div className="register-history-card__col-text">
                            <span className="register-history-card__label">
                              Date
                            </span>
                            <div className="register-history-card__value register-history-card__value--muted">
                              {dateValue}
                            </div>
                          </div>
                        </div>
                      </div>

                      <HistoryCol
                        icon={<UserOutlined />}
                        label="Customer"
                        value={customer}
                      />
                      <HistoryCol
                        icon={<ThunderboltOutlined />}
                        label="Action"
                        value={action}
                      />
                      <HistoryCol
                        icon={<SendOutlined />}
                        label="From Status"
                        value={fromStatus}
                      />
                      <HistoryCol
                        icon={<SafetyCertificateOutlined />}
                        iconClass="register-history-card__icon--success"
                        label="To Status"
                        value={toStatus}
                      />
                      <HistoryCol
                        icon={<IdcardOutlined />}
                        label="Changed By"
                        value={changedBy}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Spin>

      {pagination.total > 0 && (
        <div className="register-record-pagination">
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={handlePageChange}
            showSizeChanger={false}
          />
        </div>
      )}
    </div>
  );
};

export default BookingHistoryTable;
